import { useState, useEffect, lazy, Suspense } from 'react';
import {
	Button,
	Grid,
	IconButton,
	InputAdornment,
	LinearProgress,
	Link,
	TextField,
	Typography,
	Checkbox,
	Box,
	useTheme
} from '@mui/material';
import { authenticationPagesStyles } from 'constants/styles';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';
import { Controller, useForm } from 'react-hook-form';
import { validateEmailRegex, validatePasswordShape } from 'utils/validateForm';
import ErrorMessage from 'utils/errorMessages';
import { objectHasKeys } from 'utils/helpers';
import { resetSnackbarAlerts } from 'reducers/globalSlice';
import AloftLogo from 'assets/images/aloft_logo_white.png';
import AirControl from 'assets/images/air_control_white.png';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { amplifySignUp } from 'actions/authentication';
import { stringifyQueryObj } from 'utils/urlHelpers';
import { useTranslation } from 'react-i18next';

const AuthenticationFooter = lazy(() => import('components/authentication/AuthenticationFooter'));
const PasswordRequirementsChecklist = lazy(
	() => import('components/authentication/PasswordRequirementsChecklist')
);
const LanguageSwitcherWrap = lazy(() => import('components/elements/LanguageSwitcherWrap'));

const SignUp = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();

	const styles = {
		...authenticationPagesStyles(theme)
	};

	const queryParameters = queryString.parse(window.location.search);
	const dispatch = useDispatch();
	const [showLoader, setShowLoader] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [passwordFieldFocused, setPasswordFieldFocused] = useState(false);

	const {
		formState: { errors },
		control,
		handleSubmit,
		getValues,
		watch,
		trigger,
		setValue
	} = useForm({
		defaultValues: {
			firstname: '',
			lastname: '',
			email: queryParameters.email,
			password: '',
			confirmPassword: '',
			terms_accepted: false
		}
	});

	const passwordCurrentValue = watch('password');
	const confirmPasswordCurrentValue = watch('confirmPassword');

	const handleSignUp = async () => {
		dispatch(resetSnackbarAlerts([]));
		setShowLoader(true);
		let payload = {
			...getValues()
		};
		delete payload.terms_accepted; //Do not sent to back end
		// Trim all fields in payload
		for (const k of Object.keys(payload)) payload[k] = payload[k].trim();
		await dispatch(
			amplifySignUp({ signupPayload: payload, queryParams: queryParameters, navigate })
		);
		setShowLoader(false);
	};

	const passwordsMatch = () => {
		const { password, confirmPassword } = getValues();
		return password && confirmPassword && password === confirmPassword;
	};

	const hasErrors = objectHasKeys(errors);

	const handleClickLogin = () => {
		//Pass along any query params that were sent by the mobile app
		navigate({
			pathname: '/login',
			search: stringifyQueryObj(queryParameters)
		});
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const handleAcceptTerms = async e => {
		setValue('terms_accepted', e.target.checked);
		await trigger('terms_accepted');
	};

	useEffect(() => {
		const validatePasswords = async () => {
			await trigger(['password', 'confirmPassword']);
		};
		if (passwordCurrentValue || confirmPasswordCurrentValue) {
			validatePasswords();
		}
	}, [passwordCurrentValue, confirmPasswordCurrentValue, trigger]);

	return (
		<Suspense fallback={null}>
			<LanguageSwitcherWrap>
				<Box component={'form'} sx={styles.main} onSubmit={handleSubmit(handleSignUp)}>
					<Box sx={styles.headerContainer}>
						<Box
							component={'img'}
							loading='lazy'
							src={AloftLogo}
							alt=''
							onClick={handleClickLogin}
							sx={styles.logo}
						/>
						<Link
							href='https://www.aloft.ai/support/'
							target='_blank'
							rel='noopener noreferrer'
							underline='none'
						>
							<Button
								sx={{ ...styles.outlineButton, ...styles.topSupportButton }}
								variant='outlined'
							>
								{t('Support')}
							</Button>
						</Link>
					</Box>
					<Grid container sx={styles.mainContainer}>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.leftContainerText}>
							<Box
								component={'img'}
								loading='lazy'
								src={AirControl}
								alt=''
								sx={styles.airControlLogo}
							/>
							<Typography sx={styles.authCopy}>
								{t('Log into Air Control to manage your drone data and customize your experience')}.{' '}
								{t('Sign in with the same credentials you use to sign into the mobile apps')}.
							</Typography>
							<Typography sx={styles.authCopy}>
								{t('Check airspace, customize workflows, apply for LAANC authorizations')}.
							</Typography>
							<Typography sx={styles.authCopy}>
								{t('If you already have an account')}
								<br />
								{t('You can')}{' '}
								<Box component={'span'} sx={styles.actionLink} onClick={handleClickLogin}>
									{t('Sign in here!')}
								</Box>
							</Typography>
						</Grid>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.rightContainer}>
							<Box elevation={0} sx={styles.outlineContainer}>
								<Box sx={styles.formTitle}>
									{t('Sign Up')}
									{showLoader && <LinearProgress />}
								</Box>

								<Controller
									render={({ field }) => (
										<TextField
											{...field}
											placeholder={t('Email')}
											variant='outlined'
											disabled={showLoader}
											sx={styles.textFieldNoMargin}
											autoFocus
										/>
									)}
									name='email'
									control={control}
									rules={{
										required: true,
										validate: {
											emailAddress: validateEmailRegex
										}
									}}
								/>
								<ErrorMessage name='email' error={errors.email} sx={styles.error} />
								<Controller
									render={({ field }) => (
										<TextField
											{...field}
											placeholder={t('First Name')}
											variant='outlined'
											disabled={showLoader}
											sx={styles.textField}
											autoFocus
										/>
									)}
									name='firstname'
									control={control}
									rules={{ required: true }}
								/>
								<ErrorMessage name='firstname' error={errors.firstname} sx={styles.error} />
								<Controller
									render={({ field }) => (
										<TextField
											{...field}
											placeholder={t('Last Name')}
											variant='outlined'
											disabled={showLoader}
											sx={styles.textField}
											autoFocus
										/>
									)}
									name='lastname'
									control={control}
									rules={{ required: true }}
								/>
								<ErrorMessage name='lastname' error={errors.lastname} sx={styles.error} />
								<Controller
									render={({ field }) => (
										<TextField
											{...field}
											onFocus={() => setPasswordFieldFocused(true)}
											placeholder={t('Password')}
											inputProps={{ type: showPassword ? 'text' : 'password' }}
											variant='outlined'
											disabled={showLoader}
											sx={styles.textField}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														<IconButton onClick={handleClickShowPassword} tabIndex='-1'>
															{showPassword ? <Visibility /> : <VisibilityOff />}
														</IconButton>
													</InputAdornment>
												)
											}}
										/>
									)}
									name='password'
									control={control}
									rules={{
										required: true,
										validate: {
											passwordShape: validatePasswordShape,
											passwordMismatch: passwordsMatch
										}
									}}
								/>
								{(passwordFieldFocused || errors.password) && (
									<Suspense fallback={null}>
										<PasswordRequirementsChecklist value={passwordCurrentValue} />
									</Suspense>
								)}
								<Controller
									render={({ field }) => (
										<TextField
											{...field}
											placeholder={t('Confirm Password')}
											inputProps={{ type: showConfirmPassword ? 'text' : 'password' }}
											variant='outlined'
											disabled={showLoader}
											sx={styles.textField}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														<IconButton onClick={handleClickShowConfirmPassword} tabIndex='-1'>
															{showConfirmPassword ? <Visibility /> : <VisibilityOff />}
														</IconButton>
													</InputAdornment>
												)
											}}
										/>
									)}
									name='confirmPassword'
									control={control}
									rules={{
										required: true,
										validate: {
											passwordMismatch: passwordsMatch
										}
									}}
								/>
								<ErrorMessage
									name='confirmPassword'
									error={errors.confirmPassword}
									sx={styles.error}
								/>
								<Box sx={styles.termsCheckbox}>
									<Controller
										render={() => (
											<Box sx={styles.checkboxContainer}>
												<Checkbox
													disabled={showLoader}
													onChange={e => handleAcceptTerms(e)}
													checked={getValues().terms_accepted}
													onKeyPress={async e => {
														if (e.key === 'Enter') {
															setValue('terms_accepted', true);
															await trigger('terms_accepted');
														}
													}}
												/>
												<span>
													{t('By signing up for Air Control, you agree to our')}{' '}
													<Link
														href='https://www.aloft.ai/terms'
														target='_blank'
														rel='noopener noreferrer'
														underline='none'
														sx={styles.actionLink}
													>
														{t('Terms of Use')}
													</Link>{' '}
													{t('and')}{' '}
													<Link
														href='https://www.aloft.ai/privacy'
														target='_blank'
														rel='noopener noreferrer'
														underline='none'
														sx={styles.actionLink}
													>
														{t('Privacy Policy')}
													</Link>
												</span>
											</Box>
										)}
										name='terms_accepted'
										control={control}
										rules={{
											required: true
										}}
									/>
								</Box>
								<ErrorMessage
									name='terms_accepted'
									error={errors.terms_accepted}
									sx={styles.error}
								/>
								<Button
									color='primary'
									sx={styles.button}
									variant='contained'
									id='signUp'
									type='submit'
									disabled={showLoader || hasErrors}
								>
									{t('Submit')}
								</Button>
							</Box>
							<Box sx={styles.belowSignIn}>
								<Typography>
									{t(
										'Log into Air Control to manage your drone data and customize your experience'
									)}
									. {t('Sign in with the same credentials you use to sign into the mobile apps')}.{' '}
									{t('Check airspace, customize workflows, apply for LAANC authorizations')}.
								</Typography>
								<Typography>
									{t('If you already have an account you can')}{' '}
									<Box component={'span'} sx={styles.actionLink} onClick={handleClickLogin}>
										{t('Sign in here!')}
									</Box>
								</Typography>
								<Link
									href='https://www.aloft.ai/support/'
									target='_blank'
									rel='noopener noreferrer'
									underline='none'
									sx={styles.bottomSupportButton}
								>
									<Button sx={styles.outlineButton} variant='outlined' fullWidth>
										{t('Support')}
									</Button>
								</Link>
							</Box>
						</Grid>
					</Grid>
					<Suspense fallback={null}>
						<AuthenticationFooter />
					</Suspense>
				</Box>
			</LanguageSwitcherWrap>
		</Suspense>
	);
};

export default SignUp;
